import * as R from 'ramda';
import * as yup from 'yup';
import i18n from 'helpers/i18n';
import messages from './messages';
import { useState } from 'react';
import { Wrapper, Title } from './Styled';
import { MetaDataOption, MetaDataOptionProps } from './MetaDataOption';
import { CycleMetaDataValue } from 'types/api/envScales/CycleMetaDataValue';
import { MetaDataCategory } from 'types/api/envScales/MetaDataCategory';
import { ObservationCycle } from 'types/api/envScales/ObservationCycle';
import { updateCycleMetaData } from 'actions/envScales/observationCycles';
import { getInitialValues, getPayloadValues, SelectedValues } from './utils';
import { ErrorMessage } from 'components/Measure/View/Styled';
import useDebouncedEffect from 'hooks/useDebouncedEffect';
import { useServerFieldError } from 'components/Measure/View/ValidationsContext';
import { MetaDataCheckBox } from './MetaDataCheckBox';

interface MetaDataRatingProps {
  /** Observation ID. */
  observationId: number;

  /** Cycle */
  cycle: ObservationCycle;

  /** Initial meta data values. */
  values: CycleMetaDataValue[];

  /** Meta data categories. */
  categories: MetaDataCategory[];
}

function MetaDataRating({
  observationId,
  cycle,
  values: initialValues,
  categories
}: MetaDataRatingProps) {
  const [touched, setTouched] = useState(false);
  const [formatTouched, setFormatTouched] = useState(false);
  const contentCategories = categories.find(
    category => category.t_name === 'content'
  );
  const formatCategories = categories.find(
    category => category.t_name === 'format'
  );
  const [values, setValues] = useState(
    getInitialValues(contentCategories!, initialValues)
  );
  const [formatValues, setFormatValues] = useState(
    getInitialValues(formatCategories!, initialValues)
  );

  const checkedContentChoiceNames = Object.keys(values).filter(
    name => values[name].isChecked
  );
  const primaryContentChoiceNames = Object.keys(values).filter(
    name => values[name].isPrimary
  );
  const checkedFormatChoiceNames = Object.keys(formatValues).filter(
    name => formatValues[name].isChecked
  );

  const contentCategoryName = contentCategories?.t_name ?? 'content';
  const formatCategoryName = formatCategories?.t_name ?? 'format';

  const contentError = useServerFieldError(
    `cycles.${cycle.cycle_number}.details.${contentCategoryName}.error`,
    checkedContentChoiceNames,
    yup.array().min(1)
  );
  const contentPrimaryError = useServerFieldError(
    `cycles.${cycle.cycle_number}.details.${contentCategoryName}.primary_error`,
    primaryContentChoiceNames,
    yup.array().min(1)
  );
  // The format side has no primary/star control (see MetaDataCheckBox), so
  // only a "pick at least one" error applies here.
  const formatError = useServerFieldError(
    `cycles.${cycle.cycle_number}.details.${formatCategoryName}.error`,
    checkedFormatChoiceNames,
    yup.array().min(1)
  );

  const contentAreas: any = categories
    .filter(category => category.t_name === 'content')
    .map(content => ({
      id: content.id,
      name: content.name,
      description: content.description,
      choices: content.choices
    }));

  const contentFormats: any = categories
    .filter(category => category.t_name === 'format')
    .map(format => ({
      id: format.id,
      name: format.name,
      description: format.description,
      choices: format.choices
    }));

  const handleDebouncedUpdate = (
    touched: boolean,
    values: SelectedValues,
    categoryId: number
  ) => {
    if (!touched) {
      return;
    }

    const payload = {
      meta_data: {
        meta_data_category_id: categoryId,
        values: getPayloadValues(values)
      }
    };

    updateCycleMetaData(observationId, cycle.id, payload);
  };

  useDebouncedEffect(
    () =>
      contentCategories &&
      handleDebouncedUpdate(touched, values, contentCategories.id),
    1000,
    [values, touched]
  );

  useDebouncedEffect(
    () =>
      formatCategories &&
      handleDebouncedUpdate(formatTouched, formatValues, formatCategories.id),
    1000,
    [formatValues, formatTouched]
  );

  const handleMetaDataChange = (
    data: { name: string; value: { isPrimary: boolean; [key: string]: any } },
    currentValues: SelectedValues,
    setValuesFn: React.Dispatch<React.SetStateAction<SelectedValues>>,
    setTouchedFn: React.Dispatch<React.SetStateAction<boolean>>
  ) => {
    let newValues = { ...currentValues };
    const isNewPrimary = data.value.isPrimary;

    if (isNewPrimary) {
      newValues = R.mapObjIndexed(
        value => ({ ...value, isPrimary: false }),
        newValues
      );
    }

    const mergedValues = {
      ...newValues,
      [data.name]: {
        ...data.value,
        choiceId: data.value.choiceId,
        isChecked: data.value.isChecked ?? false
      }
    };
    setValuesFn(mergedValues);
    setTouchedFn(true);
  };

  const handleChange: MetaDataOptionProps['onChange'] = data => {
    handleMetaDataChange(data, values, setValues, setTouched);
  };

  const handleFormatChange: MetaDataOptionProps['onChange'] = data => {
    handleMetaDataChange(data, formatValues, setFormatValues, setFormatTouched);
  };

  const contentErrorMessage = [
    contentError?.message,
    contentPrimaryError?.message
  ]
    .filter(Boolean)
    .join(' ');

  function verifyCategoryData() {
    if (
      R.isEmpty(categories) ||
      categories.length < 2 ||
      contentCategories?.t_name !== 'content' ||
      formatCategories?.t_name !== 'format' ||
      contentCategories?.choices?.length === 0 ||
      formatCategories?.choices?.length === 0
    ) {
      return true;
    }

    return false;
  }

  if (verifyCategoryData()) {
    return null;
  }

  return (
    <Wrapper>
      <div className="grid grid-cols-1 md:grid-cols-12 md:gap-4">
        <div className="col-span-5">
          {contentAreas.map((contentArea: any) => (
            <div key={contentArea.id} className="mb-[24px]">
              <Title>
                {i18n.ft(messages.type, { category: contentArea.name })}
              </Title>
              <div>{contentArea.description}</div>

              {contentErrorMessage && (
                <ErrorMessage>{contentErrorMessage}</ErrorMessage>
              )}

              <div className="mt-[32px]">
                {contentArea.choices.map((choice: any) => (
                  <MetaDataOption
                    key={`${contentArea.id}-${choice.id}`}
                    choice={choice}
                    value={values[choice.t_name]}
                    onChange={handleChange}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="col-span-2 w-[10px] rounded-[10px] bg-[#f4f8fa] mx-auto"></div>

        <div className="col-span-5">
          {contentFormats.map((contentFormat: any) => (
            <div key={contentFormat.id} className="mb-[24px]">
              <Title>
                {i18n.ft(messages.type, { category: contentFormat.name })}
              </Title>
              <div>{contentFormat.description}</div>

              {formatError?.message && (
                <ErrorMessage>{formatError.message}</ErrorMessage>
              )}

              <div className="mt-[32px]">
                {contentFormat.choices.map((choice: any) => (
                  <MetaDataCheckBox
                    key={`${contentFormat.id}-${choice.id}`}
                    choice={choice}
                    value={formatValues[choice.t_name]}
                    onChange={handleFormatChange}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </Wrapper>
  );
}

export default MetaDataRating;
